/* eslint-disable no-unused-vars */
/* eslint-disable max-lines */
import {
  Search,
  Filter,
  SortAsc,
  EyeOff,
  Group,
  Trash2,
  RefreshCcw,
  Plus,
} from 'lucide-react';
import {
 Button
} from 'shared/components/ui/button';
import {
 Input
} from 'shared/components/ui/input';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from 'shared/components/ui/tooltip';
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from 'shared/components/ui/dropdown-menu';

// Define types for the props
import {
 Column
} from './types/builderRecords.types';

interface RecordsHeaderProps {
  searchTerm: string;
  setSearchTerm: React.Dispatch<React.SetStateAction<string>>;
  filterColumn: string | null;
  setFilterColumn: React.Dispatch<React.SetStateAction<string | null>>;
  filterValue: string;
  setFilterValue: React.Dispatch<React.SetStateAction<string>>;
  groupByColumn: string | null;
  setGroupByColumn: React.Dispatch<React.SetStateAction<string | null>>;
  sortColumn: string | null;
  setSortColumn: React.Dispatch<React.SetStateAction<string | null>>;
  sortDirection: 'asc' | 'desc' | null;
  setSortDirection: React.Dispatch<React.SetStateAction<'asc' | 'desc' | null>>;
  hiddenColumns: string[];
  setHiddenColumns: React.Dispatch<React.SetStateAction<string[]>>;
  columns: Column[];
  hasUnsavedChanges: boolean;
  onSaveCreatedRecords: () => void;
  onSaveModifiedRecords: () => void;
  onDiscardChanges: () => void;
  updatedRows: any[];
  createdRows: any[];
  selectedRows: string[];
  setDelModal: React.Dispatch<React.SetStateAction<boolean>>;
  isDeleting: boolean;
  setRowsToDelete: React.Dispatch<React.SetStateAction<string[]>>;
  onRefreshRecords: () => void;
  isLoading: boolean;
  onAddRow: () => void;
  tableId: string | null;
}

export function RecordsHeader({
  searchTerm,
  setSearchTerm,
  filterColumn,
  setFilterColumn,
  filterValue,
  setFilterValue,
  groupByColumn,
  setGroupByColumn,
  sortColumn,
  setSortColumn,
  sortDirection,
  setSortDirection,
  hiddenColumns,
  setHiddenColumns,
  columns,
  hasUnsavedChanges,
  onSaveCreatedRecords,
  onSaveModifiedRecords,
  onDiscardChanges,
  updatedRows,
  createdRows,
  selectedRows,
  setDelModal,
  isDeleting,
  setRowsToDelete,
  onRefreshRecords,
  isLoading,
  onAddRow,
  tableId = null,
}: RecordsHeaderProps) {
  return (
      <div className="flex items-center justify-between gap-2 border-b p-3 bg-muted/30">
          <div className="flex items-center gap-2 flex-1">
              <div className="relative flex-1 max-w-md">
                  <Search className="absolute left-2 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                  <Input
            placeholder="Search records..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-8 pr-4"
          />
              </div>
          </div>
          <div className="flex items-center gap-2 flex-wrap justify-end">
              {/* Add record button placed on the same line with other buttons */}

              <tr>
                  <th
            colSpan={columns.length + 2 - hiddenColumns.length}
            className="text-left"
          >
                      {tableId !== 'User' && (
                      <Button
                variant="outline"
                size="sm"
                className="gap-1"
                onClick={onAddRow}
                disabled={updatedRows.length > 0}
              >
                          <Plus className="h-4 w-4" />
                          <span>Add record</span>
                      </Button>
            )}
                  </th>
              </tr>

              {/* Handle unsaved changes */}
              {hasUnsavedChanges && (
              <>
                  <TooltipProvider>
                      <Tooltip>
                          <TooltipTrigger asChild>
                              <>
                                  {createdRows.length > 0 && (
                                  <Button
                        size="sm"
                        className="gap-1"
                        onClick={onSaveCreatedRecords}
                      > Save new records ({createdRows.length})
                                  </Button>
                    )}
                                  {updatedRows.length > 0 && createdRows.length === 0 && (
                                  <Button
                        size="sm"
                        className="gap-1"
                        onClick={onSaveModifiedRecords}
                      > Save modified records ({updatedRows.length})
                                  </Button>
                    )}
                              </>
                          </TooltipTrigger>
                          <TooltipContent>Save all changes to the table</TooltipContent>
                      </Tooltip>
                  </TooltipProvider>
                  <TooltipProvider>
                      <Tooltip>
                          <TooltipTrigger asChild>
                              <Button
                    variant="outline"
                    size="sm"
                    className="gap-1"
                    onClick={onDiscardChanges}
                  >
                                  Discard Changes
                              </Button>
                          </TooltipTrigger>
                          <TooltipContent>Discard all unsaved changes</TooltipContent>
                      </Tooltip>
                  </TooltipProvider>
              </>
        )}

              {selectedRows.length > 0 && (
              <TooltipProvider>
                  <Tooltip>
                      <TooltipTrigger asChild>
                          <Button
                  variant="destructive"
                  size="sm"
                  className="gap-1"
                  onClick={(e) => {
                    e.stopPropagation();
                    setRowsToDelete(selectedRows);
                    setDelModal(true);
                  }}
                  disabled={isDeleting}
                >
                              <Trash2 className="h-4 w-4" />
                              <span>
                                  {isDeleting
                      ? 'Deleting...'
                      : `Delete Selected (${selectedRows.length})`}
                              </span>
                          </Button>
                      </TooltipTrigger>
                      <TooltipContent>Delete selected fields</TooltipContent>
                  </Tooltip>
              </TooltipProvider>
        )}

              {/* Refresh button */}
              <TooltipProvider>
                  <Tooltip>
                      <TooltipTrigger asChild>
                          <Button
                variant="outline"
                size="sm"
                className="gap-1"
                disabled={isLoading}
                onClick={onRefreshRecords}
              >
                              <RefreshCcw className="h-5 w-5" />
                              <span>Refresh</span>
                          </Button>
                      </TooltipTrigger>
                      <TooltipContent>Refresh table data</TooltipContent>
                  </Tooltip>
              </TooltipProvider>

              {/* Filter dropdown */}
              <DropdownMenu>
                  <TooltipProvider>
                      <Tooltip>
                          <TooltipTrigger asChild>
                              <DropdownMenuTrigger asChild>
                                  <Button
                    variant="outline"
                    size="sm"
                    className="gap-1"
                    disabled={isLoading}
                  >
                                      <Filter className="h-4 w-4" />
                                      <span>Filter</span>
                                      {filterColumn && (
                                      <span className="ml-1 text-xs bg-primary/20 px-1 rounded">
                                          1
                                      </span>
                    )}
                                  </Button>
                              </DropdownMenuTrigger>
                          </TooltipTrigger>
                          <TooltipContent>Filter records by column</TooltipContent>
                      </Tooltip>
                  </TooltipProvider>
                  <DropdownMenuContent>
                      <DropdownMenuLabel>Filter by column</DropdownMenuLabel>
                      <DropdownMenuSeparator />
                      {filterColumn && (
                      <DropdownMenuItem
                onClick={() => {
                  setFilterColumn(null);
                  setFilterValue('');
                }}
              >
                          Clear filter
                      </DropdownMenuItem>
            )}
                      <DropdownMenuSeparator />
                      {columns
              .filter((col) => !!col.columnName)
              .map((col) => (
                  <DropdownMenuCheckboxItem
                  key={col.columnName}
                  checked={filterColumn === col.columnName}
                  onCheckedChange={() => setFilterColumn(col.columnName)}
                >
                      {col.columnName}
                  </DropdownMenuCheckboxItem>
              ))}
                  </DropdownMenuContent>
              </DropdownMenu>

              {filterColumn && (
              <Input
            placeholder="Filter value..."
            value={filterValue}
            onChange={(e) => setFilterValue(e.target.value)}
            className="w-40"
          />
        )}

              {/* Group dropdown */}
              <DropdownMenu>
                  <TooltipProvider>
                      <Tooltip>
                          <TooltipTrigger asChild>
                              <DropdownMenuTrigger asChild>
                                  <Button variant="outline" size="sm" className="gap-1">
                                      <Group className="h-4 w-4" />
                                      <span>Group</span>
                                      {groupByColumn && (
                                      <span className="ml-1 text-xs bg-primary/20 px-1 rounded">1
                                      </span>
                    )}
                                  </Button>
                              </DropdownMenuTrigger>
                          </TooltipTrigger>
                          <TooltipContent>Group records by column</TooltipContent>
                      </Tooltip>
                  </TooltipProvider>
                  <DropdownMenuContent>
                      <DropdownMenuLabel>Group by column</DropdownMenuLabel>
                      <DropdownMenuSeparator />
                      {groupByColumn && (
                      <DropdownMenuItem onClick={() => setGroupByColumn(null)}>
                          Clear grouping
                      </DropdownMenuItem>
            )}
                      <DropdownMenuSeparator />
                      {columns.map((column) => (
                          <DropdownMenuCheckboxItem
                key={column.id}
                checked={groupByColumn === column.id}
                onCheckedChange={() =>
                  setGroupByColumn(
                    groupByColumn === column.id ? null : column.id
                  )
                }
              >
                              {column.name}
                          </DropdownMenuCheckboxItem>
            ))}
                  </DropdownMenuContent>
              </DropdownMenu>

              {/* Sort dropdown */}
              <DropdownMenu>
                  <TooltipProvider>
                      <Tooltip>
                          <TooltipTrigger asChild>
                              <DropdownMenuTrigger asChild>
                                  <Button variant="outline" size="sm" className="gap-1">
                                      <SortAsc className="h-4 w-4" />
                                      <span>Sort</span>
                                      {sortColumn && (
                                      <span className="ml-1 text-xs bg-primary/20 px-1 rounded">
                                          1
                                      </span>
                    )}
                                  </Button>
                              </DropdownMenuTrigger>
                          </TooltipTrigger>
                          <TooltipContent>Sort records by column</TooltipContent>
                      </Tooltip>
                  </TooltipProvider>
                  <DropdownMenuContent>
                      <DropdownMenuLabel>Sort by column</DropdownMenuLabel>
                      <DropdownMenuSeparator />
                      {sortColumn && (
                      <DropdownMenuItem
                onClick={() => {
                  setSortColumn(null);
                  setSortDirection('asc');
                }}
              >
                          Clear sorting
                      </DropdownMenuItem>
            )}
                      <DropdownMenuSeparator />
                      {columns.map((column) => (
                          <DropdownMenuItem
                key={column.id}
                onClick={() => {
                  setSortColumn(column.id);
                  setSortDirection(
                    sortColumn === column.id && sortDirection === 'asc'
                      ? 'desc'
                      : 'asc'
                  );
                }}
              >
                              {column.name}{' '}
                              {sortColumn === column.id &&
                  (sortDirection === 'asc' ? '↑' : '↓')}
                          </DropdownMenuItem>
            ))}
                  </DropdownMenuContent>
              </DropdownMenu>

              {/* Hide fields dropdown */}
              <DropdownMenu>
                  <TooltipProvider>
                      <Tooltip>
                          <TooltipTrigger asChild>
                              <DropdownMenuTrigger asChild>
                                  <Button variant="outline" size="sm" className="gap-1">
                                      <EyeOff className="h-4 w-4" />
                                      <span>Hide fields</span>
                                      {hiddenColumns.length > 0 && (
                                      <span className="ml-1 text-xs bg-primary/20 px-1 rounded">
                                          {hiddenColumns.length}
                                      </span>
                    )}
                                  </Button>
                              </DropdownMenuTrigger>
                          </TooltipTrigger>
                          <TooltipContent>Show or hide columns</TooltipContent>
                      </Tooltip>
                  </TooltipProvider>
                  <DropdownMenuContent align="end" className="w-64 p-0">
                      <div className="py-2 max-h-[300px] overflow-y-auto">
                          {columns.map((column) => (
                              <div
                  key={column.id}
                  className="flex items-center px-3 py-2 hover:bg-muted/50"
                >
                                  <div className="flex items-center flex-1">
                                      <div className="mr-2">
                                          <div
                        className={`relative inline-flex h-5 w-9 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out ${
                          !hiddenColumns.includes(column.id)
                            ? 'bg-primary'
                            : 'bg-gray-200'
                        }`}
                        onClick={() => {
                          if (hiddenColumns.includes(column.id)) {
                            setHiddenColumns((prev) =>
                              prev.filter((id) => id !== column.id)
                            );
                          } else {
                            setHiddenColumns((prev) => [...prev, column.id]);
                          }
                        }}
                      >
                                              <span
                          className={`pointer-events-none inline-block h-4 w-4 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                            !hiddenColumns.includes(column.id)
                              ? 'translate-x-4'
                              : 'translate-x-0'
                          }`}
                        />
                                          </div>
                                      </div>
                                      <span className="text-sm">{column.name}</span>
                                  </div>
                              </div>
              ))}
                      </div>
                      <div className="flex items-center justify-between p-2 border-t bg-muted/20">
                          <Button
                variant="outline"
                size="sm"
                onClick={() => setHiddenColumns(columns.map((col) => col.id))}
                className="text-xs h-8"
              >
                              Hide All
                          </Button>
                          <Button
                variant="outline"
                size="sm"
                onClick={() => setHiddenColumns([])}
                className="text-xs h-8"
              >
                              Show All
                          </Button>
                      </div>
                  </DropdownMenuContent>
              </DropdownMenu>
          </div>
      </div>
  );
}
